import {
  useQuery,
  UseQueryOptions,
  QueryKey,
} from "@tanstack/react-query";
import { appToast } from "../components/notification/app-toast";
import { useAppDispatch } from "../store";

type ThunkAction<TData, TVariables> = (variables: TVariables) => (
  dispatch: any
) => Promise<TData>;

export interface UseQueryWithDispatchOptions<TData, TVariables>
  extends Omit<
    UseQueryOptions<TData, Error, TData, QueryKey>,
    "queryKey" | "queryFn" | "onError"
  > {
  queryKey: QueryKey;
  thunkAction: ThunkAction<TData, TVariables>;
  variables: TVariables;
  showErrorToast?: boolean;
  onError?: (error: Error) => void;
}


export function useQueryWithDispatch<TData = any, TVariables = void>({
  queryKey,
  thunkAction,
  variables,
  enabled = true,
  showErrorToast = false,
  onError,
  ...restOptions
}: UseQueryWithDispatchOptions<TData, TVariables>) {
  const dispatch = useAppDispatch();

  return useQuery<TData, Error, TData, QueryKey>({
    ...restOptions,
    queryKey,
    retry: restOptions.retry ?? false,
    queryFn: async () => {
      try {
        const thunk = thunkAction(variables);
        const res = await dispatch(thunk);

        const raw = res as { success?: boolean; message?: string };
        if (raw?.success === false) {
          throw new Error(raw?.message || "Request failed");
        }

        return res as TData;
      } catch (err) {
        const error = err instanceof Error ? err : new Error(String(err));
        if (showErrorToast) {
          appToast.error(error?.message || "An unexpected error occurred");
        }
        onError?.(error);
        throw error;
      }
    },
    enabled,
  });
}
